import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Table,
  TableCell,
  TableRow,
  Button,
  InputAdornment,
  IconButton,
  Popover,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { styled } from "@mui/system";
import {
  formatToDisplayDate,
  formatToInternalDate,
  isValidDisplayDate,
  parseLocalDate,
} from "../utils/dateUtils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";

export const MainContainer = styled(Box)({
  padding: "16px",
  minHeight: "100vh",
});

export const HeaderContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "24px",
  padding: "8px 0",
  borderBottom: "2px solid #ccc",
});

export const ContentContainer = styled(Box)({
  borderRadius: "8px",
});

export const TitleSection = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
});

export const MainTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: "600",
  color: "#212121",
  letterSpacing: "-0.01em",
});

export const FilterSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
});

export const FilterRow = styled(Box)({
  display: "flex",
  gap: "20px",
  alignItems: "center",
});

export const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    height: "42px",
    backgroundColor: "#fafafa",
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      color: "grey",
    },
    "& fieldset": {
      borderColor: "#e0e0e0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#9e9e9e",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#424242",
      borderWidth: "1px",
    },
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    color: "#424242",
    fontWeight: "500",
    "&.Mui-focused": {
      color: "#212121",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#212121",
    "&::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },
  },
});

export const StyledSelect = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    height: "42px",
    "& fieldset": {
      borderColor: "#e0e0e0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#9e9e9e",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#424242",
      borderWidth: "1px",
    },
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    color: "#424242",
    fontWeight: "500",
    "&.Mui-focused": {
      color: "#212121",
    },
  },
  "& .MuiSelect-select": {
    color: "#212121",
  },
});

export const DarkButton = styled(Button)({
  borderRadius: "6px",
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "none",
  backgroundColor: "#424242",
  color: "#fff",
  minHeight: "42px",
  "&:hover": {
    backgroundColor: "#212121",
  },
  "&:disabled": {
    backgroundColor: "#e0e0e0",
    color: "#9e9e9e",
  },
});

export const StyledTable = styled(Table)({
  marginBottom: "24px",
  width: "100%",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  overflow: "hidden",
});

export const StyledHeaderCell = styled(TableCell)({
  backgroundColor: "#f8f9fa",
  fontWeight: "500",
  fontSize: "12px",
  padding: "8px 6px",
  color: "#424242",
  borderBottom: "1px solid #e0e0e0",
  boxSizing: "border-box",
  "&:not(:last-child)": {
    borderRight: "1px solid #f0f0f0",
  },
});

export const StyledBodyCell = styled(TableCell)({
  padding: "4px 6px",
  fontSize: "13px",
  color: "#212121",
  lineHeight: "1.3",
  boxSizing: "border-box",
  borderBottom: "1px solid #f0f0f0",
  "&:not(:last-child)": {
    borderRight: "1px solid #f0f0f0",
  },
});

export const StyledBodyRow = styled(TableRow)({
  "&:hover": {
    backgroundColor: "#f9f9f9",
  },
  "&:last-child td": {
    borderBottom: "none",
  },
});

export const EditableTextField = styled(TextField)({
  width: "100%",
  "& .MuiInputBase-root": {
    height: "32px",
    padding: 0,
  },
  "& .MuiInputBase-input": {
    padding: "4px 8px",
    fontSize: "12.5px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#424242",
    },
  },
});

export const EmptyState = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "40px",
  color: "#9e9e9e",
  fontSize: "14px",
});

export const ActionsContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 0",
  borderTop: "1px solid #e0e0e0",
  marginTop: "24px",
  flexWrap: "wrap",
  gap: "16px",
});

export const LeftActions = styled(Box)({
  display: "flex",
  gap: "16px",
  alignItems: "center",
  flexWrap: "wrap",
});

export const RightActions = styled(Box)({
  display: "flex",
  gap: "16px",
  alignItems: "center",
  flexWrap: "wrap",
});

// Componente DateInput: reemplaza al DatePicker de MUI en toda pantalla nueva (ver Frontend-Arquitectura-Vistas / Diseño-Componentes).
export const DateInput = ({
  label,
  value,
  onChange,
  readOnly = false,
  disabled = false,
  size = "small",
  sx = {},
  minDate = null,
  maxDate = null,
  helperText: externalHelperText = "",
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  // Último valor que el input le pasó al padre: cuando vuelve por props no se pisa lo que se está tipeando
  const lastEmittedRef = useRef(undefined);

  const emit = (newValue) => {
    lastEmittedRef.current = newValue;
    if (newValue !== value) onChange(newValue);
  };

  const showError = error || !!externalHelperText;

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    if (value) {
      setDisplayValue(formatToDisplayDate(value));
      setError(false);
      setHelperText("");
    } else {
      setDisplayValue("");
      setError(false);
      setHelperText("");
    }
  }, [value]);

  const isDateInRange = (dateString) => {
    if (!dateString) return true;

    const date = new Date(dateString);

    if (minDate) {
      const min = new Date(minDate);
      if (date < min) {
        return false;
      }
    }

    if (maxDate) {
      const max = new Date(maxDate);
      if (date > max) {
        return false;
      }
    }

    return true;
  };

  const getRangeErrorMessage = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (minDate) {
      const min = new Date(minDate);
      if (date < min) {
        return `La fecha debe ser posterior a ${formatToDisplayDate(minDate)}`;
      }
    }

    if (maxDate) {
      const max = new Date(maxDate);
      if (date > max) {
        return `La fecha debe ser anterior a ${formatToDisplayDate(maxDate)}`;
      }
    }

    return "";
  };

  const formatAsYouType = (input) => {
    const numbers = input.replace(/\D/g, "");

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  const handleChange = (e) => {
    const input = e.target.value;

    if (!/^[0-9/]*$/.test(input)) {
      return;
    }

    const formatted = formatAsYouType(input);
    setDisplayValue(formatted);

    if (formatted.length === 10) {
      if (isValidDisplayDate(formatted)) {
        const internalDate = formatToInternalDate(formatted);

        if (!isDateInRange(internalDate)) {
          setError(true);
          setHelperText(getRangeErrorMessage(internalDate));
          emit("");
        } else {
          setError(false);
          setHelperText("");
          emit(internalDate);
        }
      } else {
        setError(true);
        setHelperText("Fecha inválida");
        emit("");
      }
    } else {
      setError(false);
      setHelperText("");
      emit("");
    }
  };

  const handleBlur = () => {
    if (!displayValue) {
      setError(false);
      setHelperText("");
      emit("");
      return;
    }

    if (displayValue.length < 10) {
      setError(true);
      setHelperText("Formato incompleto. Use dd/mm/aaaa");
      return;
    }

    if (isValidDisplayDate(displayValue)) {
      const internalDate = formatToInternalDate(displayValue);

      if (!isDateInRange(internalDate)) {
        setError(true);
        setHelperText(getRangeErrorMessage(internalDate));
      } else {
        setError(false);
        setHelperText("");
        emit(internalDate);
      }
    } else {
      setError(true);
      setHelperText("Formato inválido. Use dd/mm/aaaa (ejemplo: 25/12/2024)");
    }
  };

  const handleCalendarClick = (event) => {
    if (!disabled && !readOnly) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date) => {
    if (date && date instanceof Date && !isNaN(date)) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const internalDate = `${year}-${month}-${day}`;
      const displayDate = `${day}/${month}/${year}`;

      if (!isDateInRange(internalDate)) {
        setError(true);
        setHelperText(getRangeErrorMessage(internalDate));
        setDisplayValue(displayDate);
        emit("");
        handleCalendarClose();
      } else {
        setDisplayValue(displayDate);
        setError(false);
        setHelperText("");
        emit(internalDate);
        handleCalendarClose();
      }
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ flex: 1, ...sx }}>
      <StyledTextField
        ref={inputRef}
        label={label}
        value={displayValue}
        helperText={showError ? helperText || externalHelperText : ""}
        // Fuera del flujo: el mensaje aparece debajo sin cambiar el alto del campo ni moverlo en la fila
        FormHelperTextProps={{
          sx: {
            position: "absolute",
            top: "100%",
            left: 0,
            mx: "14px",
            mt: "2px",
            whiteSpace: "nowrap",
          },
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="dd/mm/aaaa"
        size={size}
        disabled={disabled}
        error={showError}
        inputProps={{
          maxLength: 10,
        }}
        InputProps={{
          readOnly: readOnly,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleCalendarClick}
                edge="end"
                disabled={disabled || readOnly}
                size="small"
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 18, color: disabled ? "#9e9e9e" : "#616161" }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: showError ? "#d32f2f" : "#e0e0e0",
            },
          },
        }}
        {...props}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCalendarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={value ? parseLocalDate(value) : new Date()}
            onChange={handleDateSelect}
            minDate={minDate ? parseLocalDate(minDate) : undefined}
            maxDate={maxDate ? parseLocalDate(maxDate) : undefined}
            renderInput={(params) => <TextField {...params} />}
            sx={{
              "& .MuiPickersDay-root": {
                "&.Mui-selected": {
                  backgroundColor: "#9e9e9e !important",
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#757575 !important",
                  },
                },
                "&.MuiPickersDay-today:not(.Mui-selected)": {
                  border: "1px solid #bdbdbd !important",
                  backgroundColor: "transparent !important",
                  color: "inherit",
                  fontWeight: 500,
                },
              },
              "& .MuiPickersCalendarHeader-root": {
                "& .MuiIconButton-root": {
                  color: "#424242",
                },
              },
            }}
          />
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};
